import getRouterBasename from '@/lib/router';
import { toast } from 'sonner';

import { ChainlitAPI, ClientError } from '@chainlit/react-client';
import { authService } from '@/services/authService';

const devServer = 'http://localhost:8000' + getRouterBasename();
const url = import.meta.env.DEV
  ? devServer
  : window.origin + getRouterBasename();
const serverUrl = new URL(url);

const httpEndpoint = serverUrl.toString();
const backendOrigin = serverUrl.origin;
const sanitizedPath = serverUrl.pathname === '/' ? '' : serverUrl.pathname.replace(/\/$/, '');
const backendPathPrefix = sanitizedPath;
const backendPathPrefixWithSlash = backendPathPrefix ? `${backendPathPrefix}/` : '';
const httpEndpointWithTrailingSlash = httpEndpoint.endsWith('/') ? httpEndpoint : `${httpEndpoint}/`;
const authConfigUrl = new URL('auth/config', httpEndpointWithTrailingSlash).toString();

let interceptorConfigured = false;
let cachedAuthEnabled: boolean | null = null;
let cachedOriginalFetch: typeof window.fetch | null = null;

const isTeamsContext = () => {
  const path = window.location.pathname;
  const hasTeamsCookie = document.cookie.includes('teams_auth_token=');
  const teamsSdkPresent = typeof (window as any).microsoftTeams !== 'undefined';
  return path.includes('/teams-tab') || hasTeamsCookie || teamsSdkPresent;
};

const on401 = () => {
  if (isTeamsContext()) {
    if (import.meta.env.DEV) {
      console.warn('[API Interceptor] 401 encountered in Teams context – suppressing redirect');
    }
    return;
  }

  if (window.location.pathname !== getRouterBasename() + '/login') {
    window.location.href = getRouterBasename() + '/login';
  }
};

const onError = (error: ClientError) => {
  toast.error(error.toString());
};

export const apiClient = new ChainlitAPI(
  httpEndpoint,
  'webapp',
  {}, // Optional - additionalQueryParams property.
  on401,
  onError
);

/**
 * Set up request interceptor to add X-Auth-Token header
 * This ensures all API calls include the Azure AD access token
 */
export const setupAuthInterceptor = async (): Promise<boolean> => {
  const isDebug = import.meta.env.DEV;
  if (interceptorConfigured) {
    if (isDebug) {
      console.log('[API Interceptor] 🔁 Interceptor already configured; authEnabled =', cachedAuthEnabled);
    }
    return cachedAuthEnabled ?? true;
  }

  const originalFetch = (cachedOriginalFetch ?? window.fetch).bind(window);
  cachedOriginalFetch = originalFetch;

  const resolveRequestUrl = (input: RequestInfo | URL): string => {
    if (typeof input === 'string') return input;
    if (input instanceof URL) return input.href;
    return input.url;
  };

  const getAuthEnabledFlag = async () => {
    if (cachedAuthEnabled !== null) return cachedAuthEnabled;

    try {
      const response = await originalFetch(authConfigUrl, { credentials: 'include' });
      if (!response.ok) {
        if (isDebug) {
          console.warn('[API Interceptor] ⚠️ Failed to fetch auth config:', response.status, response.statusText);
        }
        cachedAuthEnabled = true;
        return cachedAuthEnabled;
      }
      const data = await response.json();
      cachedAuthEnabled = data?.authEnabled ?? true;
      if (isDebug) {
        console.log('[API Interceptor] 🔐 AUTH_ENABLED flag from backend:', cachedAuthEnabled);
      }
    } catch (error) {
      if (isDebug) {
        console.warn('[API Interceptor] ⚠️ Error fetching auth config, defaulting to true:', error);
      }
      cachedAuthEnabled = true;
    }

    return cachedAuthEnabled;
  };

  const authEnabled = await getAuthEnabledFlag();
  if (!authEnabled) {
    interceptorConfigured = true;
    if (isDebug) {
      console.log('[API Interceptor] 🚫 AUTH_ENABLED=false - interceptor not installed');
      console.log('[API Interceptor] 🔓 Requesting local dev session via header auth...');
    }
    try {
      await apiClient.headerAuth();
      if (isDebug) {
        console.log('[API Interceptor] ✅ Local dev session established (header auth)');
      }
    } catch (error) {
      console.error('[API Interceptor] ❌ Failed to establish local dev session:', error);
    }
    return authEnabled;
  }

  if (isDebug) console.log('[API Interceptor] 🔧 Setting up global fetch interceptor...');
  if (isDebug) console.log('[API Interceptor] Original fetch saved');

  // Flag to prevent recursive token acquisition
  let isAcquiringToken = false;

  // Override fetch to add authentication header
  window.fetch = async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    const url = resolveRequestUrl(input);
    if (isDebug) console.log('[API Interceptor] 🌐 Intercepted request:', url);
    
    // Skip interception for Microsoft login endpoints to avoid infinite recursion
    // (MSAL internally uses fetch to get tokens)
    if (url.includes('login.microsoftonline.com') || url.includes('login.microsoft.com')) {
      if (isDebug) console.log('[API Interceptor] ⏭️ Skipping Microsoft auth endpoints');
      return originalFetch(input, init);
    }

    // Skip interception for auth config endpoint to avoid recursion
    if (url === authConfigUrl) {
      if (isDebug) console.log('[API Interceptor] ⏭️ Skipping auth config endpoint');
      return originalFetch(input, init);
    }

    // Resolve URL to determine if the request targets our backend
    let target: URL | null = null;
    try {
      target = new URL(url, window.location.href);
    } catch {
      if (isDebug) console.warn('[API Interceptor] ⚠️ Unable to parse request URL, skipping token injection');
      return originalFetch(input, init);
    }

    const isSameOriginAsBackend = target.origin === backendOrigin;
    const matchesBackendPath =
      !backendPathPrefix ||
      target.pathname === backendPathPrefix ||
      target.pathname.startsWith(backendPathPrefixWithSlash);
    const shouldAttachToken = isSameOriginAsBackend && matchesBackendPath;

    if (!shouldAttachToken) {
      if (isDebug) console.log('[API Interceptor] ⏭️ Request outside backend scope, no token attached');
      return originalFetch(input, init);
    }

    // Skip interception if we're already acquiring a token (prevent recursion)
    if (isAcquiringToken) {
      if (isDebug) console.log('[API Interceptor] ⏭️ Skipping - already acquiring token');
      return originalFetch(input, init);
    }
    
    // Initialize init if undefined to avoid spreading undefined and preserve headers
    const requestInit: RequestInit = init ? { ...init } : {};
    const mergedHeaders = new Headers();
    if (typeof Request !== 'undefined' && input instanceof Request) {
      input.headers.forEach((value, key) => {
        mergedHeaders.append(key, value);
      });
    }
    if (init?.headers) {
      new Headers(init.headers).forEach((value, key) => {
        mergedHeaders.set(key, value);
      });
    }
    
    try {
      let token: string | null = null;
      
      // Check if we're in Teams and have a Teams token in cookie
      const cookies = document.cookie.split(';').reduce((acc, rawCookie) => {
        const cookie = rawCookie.trim();
        if (!cookie) {
          return acc;
        }

        const [key, ...rest] = cookie.split('=');
        if (!key) {
          return acc;
        }

        const value = rest.join('=');
        acc[key] = decodeURIComponent(value || '');
        return acc;
      }, {} as Record<string, string>);
      
      if (cookies['teams_auth_token']) {
        // Use Teams token from cookie
        token = cookies['teams_auth_token'];
        if (isDebug) console.log('[API Interceptor] 📱 Using Teams token from cookie');
      } else {
        // Get access token from auth service (with recursion guard)
        if (isDebug) console.log('[API Interceptor] 🔑 Acquiring token from MSAL...');
        isAcquiringToken = true;
        token = await authService.acquireToken();
      }

      if (token) {
        // Add X-Auth-Token header to request
        mergedHeaders.set('X-Auth-Token', token);

        if (isDebug) {
          console.log('[API Interceptor] ✅ X-Auth-Token header added to request');
          console.log('[API Interceptor] Token length:', token.length);
        }
      } else {
        if (isDebug) console.log('[API Interceptor] ⚠️ No token available, proceeding without X-Auth-Token');
      }
    } catch (error) {
      console.error('[API Interceptor] ❌ Failed to add auth token:', error);
    } finally {
      isAcquiringToken = false;
    }
    requestInit.headers = mergedHeaders;

    // Call original fetch
    if (isDebug) console.log('[API Interceptor] Calling original fetch...');
    const response = await originalFetch(input, requestInit);
    if (isDebug) console.log('[API Interceptor] Response status:', response.status, response.statusText);
    
    if (response.status === 401) {
      console.error('[API Interceptor] ⚠️ 401 Unauthorized - authentication failed');
    }
    
    return response;
  };

  console.log('[API Interceptor] ✅ Auth interceptor set up successfully');
  interceptorConfigured = true;
  return authEnabled;
};
